import { useState, useCallback, useRef } from "react";
import { useDataCache } from "../context/DataCacheContext";
import api from "../api";

const useCachedFetch = ({
  cacheKey,
  endpoint,
  params = {},
  limit = 15,
  ttl,
}) => {
  const {
    isFresh,
    getCache,
    setCache,
    invalidate: invalidateCache,
  } = useDataCache();

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const cursors = useRef([null]);
  const lastParams = useRef(null);

  // ← Store params in a ref so fetch never needs to be recreated
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const cacheKeyRef = useRef(cacheKey);
  const fetchRef = useRef(null);
  cacheKeyRef.current = cacheKey;

  const buildKey = useCallback(
    (pageNum) => {
      const allParams = { ...paramsRef.current, limit, page: pageNum };
      const paramStr = Object.entries(allParams)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => `${k}=${v}`)
        .join("&");
      return `${cacheKeyRef.current}:${paramStr}`;
    },
    [limit],
  ); // ← only limit, never recreated due to params

  const fetch = useCallback(
    async (pageNum = 0, force = false) => {
      const currentParamStr = JSON.stringify(paramsRef.current);

      // Reset cursors when filters change
      if (pageNum === 0 && lastParams.current !== currentParamStr) {
        cursors.current = [null];
        lastParams.current = currentParamStr;
      }

      const key = buildKey(pageNum);

      // Return cached data if still fresh
      if (!force && isFresh(key, ttl)) {
        const cached = getCache(key);
        setData(cached.data);
        setTotal(cached.total);
        setHasMore(cached.hasMore);
        setPage(pageNum);
        return;
      }

      setLoading(true);
      try {
        const cursor = cursors.current[pageNum] ?? null;

        const res = await api.get(endpoint, {
          params: {
            ...paramsRef.current, // ← always reads latest params via ref
            limit,
            lastDocId: cursor,
          },
        });

        const result = {
          data: res.data.data ?? [],
          total: res.data.total ?? 0,
          hasMore: (pageNum + 1) * limit < (res.data.total ?? 0),
          lastDocId: res.data.lastDocId ?? null,
        };

        cursors.current[pageNum + 1] = result.lastDocId;
        setCache(key, result);
        setData(result.data);
        setTotal(result.total);
        setHasMore(result.hasMore);
        setPage(pageNum);
      } catch (err) {
        console.error(`useCachedFetch error [${endpoint}]:`, err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [buildKey, isFresh, getCache, setCache, endpoint, limit, ttl],
  );
  // ← params removed from deps — paramsRef.current always has latest value

  fetchRef.current = fetch;

  const refetch = useCallback(() => {
    cursors.current = [null];
    lastParams.current = null;
    return fetch(0, true);
  }, [fetch]);

  const invalidate = useCallback(() => {
    cursors.current = [null];
    lastParams.current = null;
    invalidateCache(cacheKeyRef.current);
  }, [invalidateCache]);

  const nextPage = useCallback(() => {
    if (!hasMore || loading) return;
    fetchRef.current(page + 1);
  }, [hasMore, loading, page]);

  const prevPage = useCallback(() => {
    if (page === 0 || loading) return;
    fetchRef.current(page - 1);
  }, [page, loading]);

  return {
    data,
    total,
    loading,
    hasMore,
    page,
    fetch,
    refetch,
    invalidate,
    nextPage,
    prevPage,
  };
};

export default useCachedFetch;
